// AdapterAddModal.jsx — Form to create a custom OpenAI-compatible adapter.

import { useState } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';

export default function AdapterAddModal({ onClose, onCreate }) {
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [kind, setKind] = useState('text');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await onCreate({
      display_name: displayName,
      base_url: baseUrl,
      request_format: 'openai-compatible',
      default_model: defaultModel || null,
      api_key: apiKey || null,
      kind,
      badge: 'CUSTOM',
      icon: '🔌',
    });
    setSubmitting(false);
    if (!res.ok) setError(res.error || 'Falha ao criar adapter');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-adapter-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-5 shadow-2xl"
      >
        <header className="mb-4 flex items-center gap-2">
          <Plus size={18} aria-hidden="true" />
          <h2 id="add-adapter-title" className="text-base font-semibold">
            Adicionar adapter custom
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="ml-auto rounded p-1 hover:bg-apolo-surface2"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="flex flex-col gap-3 text-sm">
          <Field label="Nome (display)" required value={displayName} onChange={setDisplayName} placeholder="Meu provider" />
          <Field label="Base URL" required value={baseUrl} onChange={setBaseUrl} placeholder="https://api.exemplo.com/v1" />
          <Field label="Modelo padrão (opcional)" value={defaultModel} onChange={setDefaultModel} placeholder="gpt-4o" />
          <Field label="API Key (opcional)" type="password" value={apiKey} onChange={setApiKey} placeholder="sk-..." />
          <label className="flex flex-col gap-1">
            <span className="text-apolo-textSecondary">Tipo</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5"
            >
              <option value="text">Texto</option>
              <option value="image">Imagem</option>
              <option value="video">Vídeo</option>
              <option value="audio">Áudio</option>
            </select>
          </label>
          {error && (
            <div
              role="alert"
              className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-2 py-1 text-xs text-apolo-error"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-apolo-border px-3 py-1.5 text-sm hover:bg-apolo-surface2"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || !displayName || !baseUrl}
            className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-sm font-medium text-apolo-bg disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Plus size={14} aria-hidden="true" />
            )}
            Criar
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, required, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-apolo-textSecondary">
        {label}
        {required && <span className="ml-1 text-apolo-error">*</span>}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
      />
    </label>
  );
}
